import { makeAutoObservable, runInAction } from 'mobx';
import type { Agent } from '../entity';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';

export interface AgentCaseStore extends BaseStore {}

export class AgentCaseStore {
  agents: string[] = [];

  agentsLoaded = false;

  agent: Agent = undefined;

  agentsByName: Record<string, Agent> = {};

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this);
  }

  async getAgents(_force = false) {
    await this.fetchAgents();
  }

  async getAgent(name: string) {
    return this.fetchAgent(name);
  }

  addAgentName(name: string) {
    const normalizedName = String(name || '').trim();

    if (!normalizedName || this.agents.includes(normalizedName)) {
      return;
    }

    this.agents = [...this.agents, normalizedName];
  }

  removeAgentName(name: string) {
    this.agents = this.agents.filter((agentName) => agentName !== name);
  }

  scopeNameForAgentId(agentId?: string): string | undefined {
    const normalizedAgentId = String(agentId || '').trim();

    if (!normalizedAgentId) {
      return undefined;
    }

    return Object.values(this.agentsByName).find(
      (agent) => agent.agent_azure_id === normalizedAgentId
    )?.agent_name;
  }

  async fetchAgents() {
    let response: unknown;

    try {
      response = await this.api.fetch({
        endpoint: `${this.endpointAgent + '/custom'}`,
        method: 'GET'
      });
    } catch {
      runInAction(() => {
        this.agents = [];
        this.agentsLoaded = true;
      });
      return;
    }

    const agentNames = Array.isArray(response)
      ? response.map((agentName) => String(agentName))
      : [];

    if (agentNames.length === 0) {
      runInAction(() => {
        this.agents = [];
        this.agentsLoaded = true;
      });
      return;
    }

    runInAction(() => {
      this.agents = agentNames;
      this.agentsLoaded = true;
    });
  }

  async fetchAgent(name: string): Promise<Agent | undefined> {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/custom/${encodeURIComponent(name)}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      runInAction(() => {
        this.agent = undefined;
      });
      return undefined;
    }

    const nextAgent = response as Agent;
    runInAction(() => {
      this.agent = nextAgent;
      this.agentsByName = {
        ...this.agentsByName,
        [nextAgent.agent_name]: nextAgent
      };
    });
    return nextAgent;
  }

}
